import { ArrowDown, ArrowUp } from "lucide-react";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { formatBusinessDate } from "@/shared/utils/formatters";
import type { StripeFinanceSummary, StripeFinanceTotals } from "../types/stripeTypes";
import { formatStripeMinorAmount, getStripePercentChange } from "../utils/stripeMoney";

interface Props {
  totals: StripeFinanceTotals;
  summary: StripeFinanceSummary;
  /** Shows Stripe fees, refunds and disputes taken from gross income. */
  showDeductions?: boolean;
}

function NetChange({ totals, summary }: Omit<Props, "showDeductions">) {
  const previous = summary.previousPeriod;
  const baseline = previous?.totals.find((total) => total.currency === totals.currency);
  const change = getStripePercentChange(totals.netMinor, baseline?.netMinor);
  if (!previous || change === null) return null;
  const month = formatBusinessDate(previous.start, summary.period.timezone, { month: "long" });
  const Icon = change > 0 ? ArrowUp : ArrowDown;

  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <StatusBadge tone={change > 0 ? "positive" : change < 0 ? "danger" : "neutral"} className="gap-1 tabular-nums">
        {change !== 0 && <Icon aria-hidden="true" className="size-3" />}
        {change === 0 ? "No change" : <><span className="sr-only">{change > 0 ? "Up" : "Down"} </span>{Math.abs(change)}%</>}
      </StatusBadge>
      vs same days in {month}
    </p>
  );
}

export function StripeFinanceTotalsSummary({ totals, summary, showDeductions = false }: Props) {
  const stats = [
    { label: "Gross", value: formatStripeMinorAmount(totals.grossMinor, totals.currency) },
    ...(showDeductions ? [{ label: "Fees & refunds", value: formatStripeMinorAmount(totals.grossMinor - totals.netMinor, totals.currency) }] : []),
    { label: "Payments", value: totals.paymentCount.toLocaleString("en-US") },
  ];
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-muted-foreground">Net income</p>
        <p className="text-3xl font-extrabold leading-tight tabular-nums [overflow-wrap:anywhere]">
          {formatStripeMinorAmount(totals.netMinor, totals.currency)}
        </p>
        <NetChange totals={totals} summary={summary} />
      </div>
      <dl className="flex flex-wrap gap-x-6 gap-y-3">
        {stats.map((stat) => (
          <div key={stat.label} className="min-w-0">
            <dt className="text-xs text-muted-foreground">{stat.label}</dt>
            <dd className="font-bold tabular-nums [overflow-wrap:anywhere]">{stat.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
